'use client';

import { tv } from 'tailwind-variants';
import { faMinus, faPlus } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { Professional } from '@medprev-web-app/api-graphql-client';
import { useState } from 'react';

import { Button } from '@/components/ui/button/button.component';
import { useTextOverflow } from '@/app/(main)/(pages)/_components/entities/hooks/use-text-overflow';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface ProfessionalAboutProps {
  professional: Professional;
}

const professionalStyle = tv({
  slots: {
    container: [' overflow-hidden transition-all duration-500 ease-in-out'],
    text: ['text-gray-700 line-clamp-5'],
  },

  variants: {
    showMore: {
      true: {
        text: ['line-clamp-none'],
      },
    },
  },
});

export function ProfessionalAbout({ professional }: ProfessionalAboutProps) {
  const [showMore, setShowMore] = useState(false);
  const { textRef, hasOverflow, collapsedHeight, expandedHeight } = useTextOverflow();

  const { container, text } = professionalStyle();

  const analyticsEventName = showMore ? AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_ABOUT_ME : undefined;

  const containerHeight = (hasOverflow || showMore)
    ? (showMore ? expandedHeight : collapsedHeight)
    : undefined;

  return (
    <div data-testid={DataTestIdEnum.ENTITY_SECTION_ABOUT}>
      <div
        style={containerHeight ? { height: `${containerHeight}px` } : undefined}
        className={container()}
      >
        <p
          ref={textRef}
          className={text({ showMore })}
          style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
        >
          {professional.description}
        </p>
      </div>

      {(hasOverflow || showMore) && (
        <Button
          data-testid={DataTestIdEnum.ENTITY_SHOW_MORE_BUTTON}
          variant="text"
          startIcon={showMore ? faMinus : faPlus}
          onClick={() => setShowMore((prev) => !prev)}
          startIconClassName={`w-4 h-4 transition-transform duration-300 ease-in-out z-0 ${
            showMore ? 'rotate-180' : 'rotate-0'
          }`}
          className="pl-0 pr-4 py-2 gap-2 rounded-lg text-base font-semibold text-green-500 hover:underline focus:outline-none text-left justify-start"
          data-analytics-event={analyticsEventName}
        >
          {showMore ? 'Recolher' : 'Ver mais'}
        </Button>
      )}
    </div>
  );
}
